import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
    Dropdown,
    DropdownButton,
    DropdownItem,
    DropdownList,
    DropdownTrigger,
    DropdownTriggerContext,
    StickyHeader,
    Switch,
    Tab,
    Tabs,
    TabsList,
} from '@bi-digital/react';
import { CheckmarkIcon, ChevronDownIcon } from '@bi-digital/icons';
import useEmblaCarousel from 'embla-carousel-react';
import AutoHeight from 'embla-carousel-auto-height';
import Accessibility from 'embla-carousel-accessibility';
import { TextElement } from '@bino/utils/components';
import type { StudyPlan, SemesterItem } from '../../types';
import { AcademicYear } from '../academic-year/AcademicYear';
import { ItemDrawer } from '../item-drawer/ItemDrawer';
import { Legend } from '../legend/Legend';
import styles from './curriculum.module.css';

export type CurriculumProps = {
    title: string;
    description?: string;
    rawDescription?: string;
    studyPlans: StudyPlan[];
    /** The button that opens the option dropdown, shown in place of the study plan until an option is chosen. */
    chooseOptionPrompt?: string | null;
    isCourse?: boolean;
};

export const Curriculum = ({
    title,
    description,
    rawDescription,
    studyPlans,
    chooseOptionPrompt,
    isCourse,
}: CurriculumProps) => {
    const { t } = useTranslation();
    const options = [...new Set(studyPlans.map((plan) => plan.option))];
    const showDropdown = options.length > 1;
    const [option, setOption] = useState(showDropdown ? undefined : options[0]);
    const [showPartTime, setShowPartTime] = useState(false);
    const optionPlans = studyPlans.filter((plan) => plan.option === option);
    const load = showPartTime ? 'PartTime' : 'FullTime';
    const studyPlan = optionPlans.find((plan) => plan.load === load) ?? optionPlans.at(0);
    const academicYears = studyPlan?.years ?? [];
    const showTabs = academicYears.length > 1;
    const showPartTimeSwitch =
        optionPlans.some((plan) => plan.load === 'FullTime') && optionPlans.some((plan) => plan.load === 'PartTime');
    const [emblaRef, emblaApi] = useEmblaCarousel({ draggable: showTabs, duration: 20 }, [
        Accessibility(),
        AutoHeight(),
    ]);

    const [optionOpen, setOptionOpen] = useState(false);
    const [tab, setTab] = useState(0);
    const [openItem, setOpenItem] = useState<SemesterItem | null>(null);

    const toggleShowPartTime = () => {
        setShowPartTime((prev) => !prev);
        setTab(0);
    };

    const selectOption = (value: string | null) => {
        setOption(value);
        setOptionOpen(false);
        setTab(0);
    };

    const optionList = (
        <Dropdown>
            <DropdownList>
                {options.map((value) => (
                    <DropdownItem key={value}>
                        <DropdownButton
                            onClick={() => selectOption(value)}
                            endIcon={value === option && <CheckmarkIcon />}
                        >
                            {value}
                        </DropdownButton>
                    </DropdownItem>
                ))}
            </DropdownList>
        </Dropdown>
    );

    const controls = (showDropdown || option || showPartTimeSwitch) && (
        <div className={styles.controls}>
            {showDropdown ? (
                <DropdownTriggerContext open={optionOpen} onOpenChange={setOptionOpen}>
                    <DropdownTrigger
                        className={styles.optionTrigger}
                        variant='text'
                        endIcon={<ChevronDownIcon aria-hidden='true' />}
                        size='small'
                    >
                        <span className={styles.optionText}>{option}</span>
                    </DropdownTrigger>
                    {optionList}
                </DropdownTriggerContext>
            ) : (
                option && <span className={styles.chosenOption}>{option}</span>
            )}
            {showPartTimeSwitch && (
                <Switch label={t('partTime')} checked={showPartTime} onChange={toggleShowPartTime} />
            )}
        </div>
    );

    useEffect(() => {
        if (!emblaApi) return;
        emblaApi.goTo(tab);
    }, [tab, emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        // Add the transition to the root node **after** the initial render to prevent it from affecting the initial height calculation of the carousel
        emblaApi.rootNode().style.transition = 'height 0.2s';
        emblaApi.on('select', (_, ev) => setTab(ev.detail.targetSnap));
    }, [emblaApi]);

    return (
        <div className={styles.curriculum} data-color-scheme='dark' data-color='primary'>
            <StickyHeader>
                <div className={classNames(styles.sticky, styles.notStickyOnMobile)}>
                    <div className={styles.header}>
                        <h2>{title}</h2>
                    </div>
                    <hr />
                </div>
            </StickyHeader>
            {description ? (
                <p className={styles.description}>{description}</p>
            ) : (
                rawDescription && <TextElement className={styles.description} rawHtml={rawDescription} />
            )}
            <Legend academicYears={academicYears} isCourse={isCourse} />
            {showTabs && (
                <StickyHeader>
                    <div className={styles.sticky}>
                        <div className={styles.tabsRow}>
                            <Tabs value={String(tab)} onChange={(value) => setTab(Number(value))}>
                                <TabsList>
                                    {academicYears.map((academicYear, index) => (
                                        <Tab key={academicYear.id} value={String(index)}>
                                            {academicYear.title}
                                        </Tab>
                                    ))}
                                    <Tab value={String(academicYears.length)}>{t('showAll')}</Tab>
                                </TabsList>
                            </Tabs>
                            {controls}
                        </div>
                        <hr />
                    </div>
                </StickyHeader>
            )}
            {studyPlan ? (
                <div className={styles.viewport} ref={emblaRef}>
                    <div className={styles.container}>
                        {academicYears.map((academicYear) => (
                            <AcademicYear
                                className={styles.slide}
                                key={academicYear.id}
                                academicYear={academicYear}
                                hideTitle={showTabs || !controls}
                                headerEnd={controls}
                                onOpenItem={setOpenItem}
                            />
                        ))}
                        {showTabs && (
                            <div className={styles.slide}>
                                {academicYears.map((academicYear) => (
                                    <AcademicYear
                                        key={academicYear.id}
                                        academicYear={academicYear}
                                        onOpenItem={setOpenItem}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                showDropdown && (
                    <div className={styles.chooseOption}>
                        <DropdownTriggerContext open={optionOpen} onOpenChange={setOptionOpen}>
                            <DropdownTrigger variant='flat' endIcon={<ChevronDownIcon aria-hidden='true' />}>
                                {chooseOptionPrompt}
                            </DropdownTrigger>
                            {optionList}
                        </DropdownTriggerContext>
                    </div>
                )
            )}
            <ItemDrawer item={openItem} onClose={() => setOpenItem(null)} isCourse={isCourse} />
        </div>
    );
};
